<template>
  <v-layout row wrap>
    <v-flex xs12 class="pb-2">
      <v-card
        v-if="can('DASHBOARD_METRICS')"
        style="border-radius: 10px"
        :class="$vuetify.dark ? 'card-dark ' : 'card-light '"
      >
        <v-card-title>
          <span class="body-1 beez-bold pl-3">
            Metrics
          </span>
          <v-spacer></v-spacer>
          <v-btn flat icon @click="showMetrics = !showMetrics">
            <v-icon
              >{{ showMetrics ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
            </v-icon>
          </v-btn>
          <v-tooltip bottom>
            <v-btn slot="activator" flat icon @click="refreshMetrics()">
              <v-icon>cached</v-icon>
            </v-btn>
            <span>Refresh</span>
          </v-tooltip>
        </v-card-title>
        <v-layout class="row wrap" v-if="showMetrics">
          <v-progress-linear
            color="accent"
            indeterminate
            v-if="metricsLoader"
          ></v-progress-linear>
          <v-layout class="row wrap">
            <v-flex
              v-for="info in kpis"
              :key="info.id + 'flex0'"
              class="pa-1"
              lg3
              md4
              sm6
              xs12
            >
              <kpi-card
                :key="info.id + '-card'"
                :heading="info.value"
                :icon="info.icon"
                :sub-heading="info.name"
                large
              />
            </v-flex>
          </v-layout>
        </v-layout>
      </v-card>
    </v-flex>
    <v-flex xs12>
      <v-card
        v-if="can('DASHBOARD_SERVICES')"
        style="border-radius: 10px"
        :class="$vuetify.dark ? 'card-dark ' : 'card-light '"
      >
        <v-card-title>
          <span class="body-1 beez-bold pl-3">
            Trigger / Schedule {{ isBot ? 'Bots' : 'Workflows' }}
          </span>
          <v-spacer></v-spacer>
          <v-btn
            flat
            @click="isBot = !isBot"
            color="accent"
            :loading="itemLoading"
            class="text-capitalize "
            outline
          >
            Showing {{ isBot ? 'Bots' : 'Workflows' }}</v-btn
          >
          <v-list-tile-avatar>
            <v-icon>search</v-icon>
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-list-tile-sub-title>
              <v-text-field
                v-model="search"
                color="secondary"
                label="Search by Name, Description, BPMN Identifier"
                xs5
                @change="getData"
              >
              </v-text-field>
            </v-list-tile-sub-title>
          </v-list-tile-content>
          <v-tooltip bottom>
            <v-btn slot="activator" flat icon @click="clearSearch">
              <v-icon>clear</v-icon>
            </v-btn>
            <span>Clear search</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" flat icon @click="getData">
              <v-icon>cached</v-icon>
            </v-btn>
            <span>Refresh</span>
          </v-tooltip>
        </v-card-title>
        <v-data-table
          :headers="headers"
          :items="items"
          item-key="hexId"
          :loading="itemLoading"
          :pagination.sync="pagination"
          :rows-per-page-items="rowsPerPageItems"
          :total-items="pagination.totalItems"
        >
          <template slot="items" slot-scope="props">
            <tr>
              <td
                @click="props.expanded = !props.expanded"
                class="text-xs-left"
              >
                <div class="body-1 font-weight-bold">
                  {{ props.item.name }}
                </div>
              </td>
              <td
                @click="props.expanded = !props.expanded"
                class="text-xs-left"
              >
                <div class="caption">{{ props.item.description }}</div>
              </td>
              <td
                @click="props.expanded = !props.expanded"
                class="text-xs-right font-weight-black"
              >
                {{ props.item.bpmnId }}
              </td>
              <td
                @click="props.expanded = !props.expanded"
                class="text-xs-left"
              >
                {{ getLocalTime(props.item.changedAt) }}
              </td>
              <td
                @click="props.expanded = !props.expanded"
                class="text-xs-left"
              >
                {{ props.item.lastExecutedAtLocal }}
              </td>
              <td class="align-center justify-center">
                <v-flex>
                  <v-layout row wrap>
                    <v-flex x12>
                      <service-runner
                        full-screen
                        :key="props.item.hexId"
                        :automation="props.item"
                        :type="isBot ? 'Bot' : 'Workflow'"
                        :showLabel="false"
                        :ask-business-reason="false"
                        :disabled="!props.item.active"
                        v-if="can('DASHBOARD_TRIGGER')"
                      />
                    </v-flex>
                  </v-layout>
                </v-flex>
              </td>
              <td class="align-center justify-center">
                <v-flex>
                  <v-layout row wrap>
                    <v-flex x12>
                      <bot-scheduler
                        :item="props.item"
                        :type="isBot ? 'Bot' : 'Workflow'"
                        :showLabel="false"
                        :disabled="!props.item.active"
                        v-if="can('DASHBOARD_SCHEDULE')"
                      />
                    </v-flex>
                  </v-layout>
                </v-flex>
              </td>
            </tr>
          </template>
          <template slot="expand" slot-scope="props">
            <service-card
              :item="props.item"
              :type="isBot ? 'Bot' : 'Workflow'"
            />
          </template>
          <template slot="no-data">
            <v-alert :value="true" color="warning" icon="warning">
              {{
                itemLoading
                  ? 'Loading ' + isBot
                    ? 'Bots'
                    : 'Workflows' + '...'
                  : 'No ' + isBot
                  ? 'Bots'
                  : 'Workflows' + ' found!'
              }}
            </v-alert>
          </template>
          <template slot="no-results">
            <v-alert :value="true" color="warning" icon="warning">
              Your search for "{{ search }}" not found.
            </v-alert>
          </template>
        </v-data-table>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import moment from 'moment'
import ServiceRunner from '@/components/common/ServiceRunner'
import BotScheduler from '@/components/bot-scheduler/BotScheduler'
import KpiCard from '@/components/common/KpiCard'
import ServiceCard from './ServiceCard'
import _ from 'lodash'
import axios from 'axios'

export default {
  components: {
    ServiceCard,
    KpiCard,
    ServiceRunner,
    BotScheduler
  },
  name: 'my-honey',
  data() {
    return {
      isBot: false,
      dialog: false,
      search: '',
      items: [],
      itemLoading: false,
      expanded: false,
      showMetrics: true,
      metricsLoader: false,
      showServices: true,
      pagination: {
        page: 1,
        rowsPerPage: 5,
        sortBy: 'changedAt',
        descending: true,
        totalItems: 0
      },
      current: {
        page: 1,
        rowsPerPage: 5,
        sortBy: 'changedAt',
        descending: true
      },
      rowsPerPageItems: [5, 10, 25],
      headers: [
        { text: 'Name', value: 'name' },
        { text: 'Description', value: 'description' },
        { text: 'BPMN Identifier', align: 'right', value: 'bpmnId' },
        { text: 'Last Changed At', value: 'changedAt' },
        { text: 'Last Executed At', value: 'lastExecuted' },
        { text: 'Trigger', align: 'left', value: 'trigger' },
        { text: 'Schedule', align: 'left', value: 'schedule' }
      ]
    }
  },
  computed: {
    ...mapGetters({
      can: 'can'
    }),
    ...mapState({
      kpis: state => state.kpis.kpi
    })
  },
  watch: {
    isBot() {
      this.getData()
    },
    pagination: {
      handler() {
        try {
          if (
            this.pagination.page !== this.current.page ||
            this.pagination.rowsPerPage !== this.current.rowsPerPage ||
            this.pagination.sortBy !== this.current.sortBy ||
            this.pagination.descending !== this.current.descending
          ) {
            this.current.page = this.pagination.page
            this.current.rowsPerPage = this.pagination.rowsPerPage
            this.current.sortBy = this.pagination.sortBy
            this.current.descending = this.pagination.descending
            this.getData()
          }
          // eslint-disable-next-line no-empty
        } catch (error) {}
      },
      deep: true
    }
  },
  mounted() {
    if(this.kpis && this.kpis.length === 0) {
      this.refreshMetrics()
    }
    this.getData()
  },

  methods: {
    format(input) {
      return _.capitalize(_.startCase(input))
    },
    getLocalTime(input) {
      return moment(input)
        .utc()
        .local()
        .format('DD MMM YYYY, hh:mm:ss a')
    },

    getUrlParams() {
      let urlParams = ''
      //fill URL Params from current structure

      urlParams += `&page=${this.current.page}`
      urlParams += `&size=${this.current.rowsPerPage}`
      if (this.current.sortBy) {
        urlParams += `&sort=${this.current.sortBy}`
        if (this.current.descending) {
          urlParams += `,desc`
        }
      }
      if (this.search) {
        urlParams += `&search=${this.search}`
      }
      return urlParams
    },
    clearSearch() {
      this.search = ''
      this.getData()
    },
    async getData() {
      this.itemLoading = true
      let url = ''
      if (this.isBot) {
        url = '/api/v1/bots?projection=withHiveAndMetrics'
      } else {
        url = '/api/v1/workflows?projection=withMetrics'
      }
      const urlParams = this.getUrlParams()
      axios
        .get(url + urlParams)
        .then(response => {
          this.pagination.totalItems = response.data.page.totalElements
          if (response.data._embedded) {
            if (this.isBot) {
              this.items = response.data._embedded.bots.map(bot => {
                bot = this.processResp(bot)
                return bot
              })
            } else {
              this.items = response.data._embedded.workflows.map(workflow => {
                workflow = this.processResp(workflow)
                return workflow
              })
            }
          }
        })
        .catch(e => {
          console.log(e)
          this.$store.dispatch('directNotification', {
            type: 'error',
            message: 'Error fetching data',
            description:
              'Error while fetching data - ' + e.response
                ? e.response.data.message
                : 'Error unknown! Kindly retry',
            timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
          })
        })
        .finally(() => {
          this.itemLoading = false
        })
    },

    async refreshMetrics() {
      this.metricsLoader = true
      this.$store
        .dispatch('fetchKpi')
        .then(() => {
          this.metricsLoader = false
        })
        .catch(e => {
          console.log(e)
          this.metricsLoader = false
        })
        .finally(() => {
          this.metricsLoader = false
        })
    },

    processResp(obj) {
      let _self = this
      function timeConversion(time, unit) {
        time = parseFloat(time)
        switch (unit) {
          case 'Secs':
            if (time > 60) {
              time = time / 60
              if (time > 60) {
                return timeConversion(time, 'Mins')
              } else {
                return [time, 'Mins']
              }
            }
            break
          case 'Mins':
            if (time > 60) {
              time = time / 60
              return [time, 'Hrs']
            }
            break
        }
        return [time, unit]
      }

      obj.rating = 5
      obj.successRate = 0

      if (!this.isBot) {
        if (obj.workflowMetric && obj.workflowMetric.totalExecutions > 0) {
          obj.successRate =
            (
              (obj.workflowMetric.totalSuccesses /
                obj.workflowMetric.totalExecutions) *
              100
            )
              .toFixed(2)
              .toString() + ' %'
          obj.failureRate =
            (
              (obj.workflowMetric.totalFailures /
                obj.workflowMetric.totalExecutions) *
              100
            )
              .toFixed(2)
              .toString() + ' %'
          obj.totalExecution = obj.workflowMetric.totalExecutions
          obj.effortSavings = 0
          obj.avgExecutionTime = (
            obj.workflowMetric.totalRunTimeInSecs /
            obj.workflowMetric.totalExecutions
          ).toFixed(2)
          var [time, unit] = timeConversion(obj.avgExecutionTime, 'Secs')
          obj.avgExecutionTime = time.toFixed(2) + ' ' + unit
          if (obj.workflowMetric.lastExecuted) {
            obj.lastExecuted = obj.workflowMetric.lastExecuted
            obj.lastExecutedAtLocal = moment(obj.workflowMetric.lastExecuted)
              .utc()
              .local()
              .format('DD MMM YYYY hh:mm:ss a')
          }
          if (obj.workflowMetric.lastFailed) {
            obj.lastFailedAtLocal = moment(obj.workflowMetric.lastFailed)
              .utc()
              .local()
              .format('DD MMM YYYY hh:mm:ss a')
          }
          if (obj.workflowMetric.lastSuccessful) {
            obj.lastSuccessfulAtLocal = moment(
              obj.workflowMetric.lastSuccessful
            )
              .utc()
              .local()
              .format('DD MMM YYYY hh:mm:ss a')
          }
        }
      } else {
        if (obj.botMetric && obj.botMetric.totalExecutions > 0) {
          obj.successRate =
            (
              (obj.botMetric.totalSuccesses / obj.botMetric.totalExecutions) *
              100
            )
              .toFixed(2)
              .toString() + ' %'
          obj.failureRate =
            (
              (obj.botMetric.totalFailures / obj.botMetric.totalExecutions) *
              100
            )
              .toFixed(2)
              .toString() + ' %'
          obj.totalExecution = obj.botMetric.totalExecutions
          obj.effortSavings =
            obj.botMetric.totalExecutions * obj.botTemplate.effortSaved
          var [effort, effortUnit] = timeConversion(obj.effortSavings, 'Mins')
          obj.effortSavings = effort.toFixed(2) + ' ' + effortUnit
          obj.avgExecutionTime = (
            obj.botMetric.totalRunTimeInSecs / obj.botMetric.totalExecutions
          ).toFixed(2)
          var [avgtime, avgunit] = timeConversion(obj.avgExecutionTime, 'Secs')
          obj.avgExecutionTime = avgtime.toFixed(2) + ' ' + avgunit
          if (obj.botMetric.lastExecuted) {
            obj.lastExecuted = obj.botMetric.lastExecuted
            obj.lastExecutedAtLocal = moment(obj.botMetric.lastExecuted)
              .utc()
              .local()
              .format('DD MMM YYYY hh:mm:ss a')
          }
          if (obj.botMetric.lastFailed) {
            obj.lastFailedAtLocal = moment(obj.botMetric.lastFailed)
              .utc()
              .local()
              .format('DD MMM YYYY hh:mm:ss a')
          }
          if (obj.botMetric.lastSuccessful) {
            obj.lastSuccessfulAtLocal = moment(obj.botMetric.lastSuccessful)
              .utc()
              .local()
              .format('DD MMM YYYY hh:mm:ss a')
          }
        }
      }
      return obj
    }
  }
}
</script>

<style scoped>
.metric--card0 {
  background-image: linear-gradient(135deg, #ee9ae5 10%, #5961f9 100%);
  box-shadow: 0px 1px 15px 1px rgba(69, 65, 78, 0.08);
  border-radius: 5px;
}

.metric--card1 {
  background-image: linear-gradient(135deg, #36d1dc 10%, #5b86e5 100%);
  box-shadow: 0px 1px 15px 1px rgba(69, 65, 78, 0.08);
  border-radius: 5px;
}

.metric--card2 {
  background-image: linear-gradient(135deg, #a8e063 10%, #56ab2f 100%);
  box-shadow: 0px 1px 15px 1px rgba(69, 65, 78, 0.08);
  border-radius: 5px;
}

.metric--card3 {
  background-image: linear-gradient(135deg, #667eea 10%, #764ba2 100%);
  box-shadow: 0px 1px 15px 1px rgba(69, 65, 78, 0.08);
  border-radius: 5px;
}

.metric--card4 {
  background-image: linear-gradient(135deg, #48c6ef 10%, #6f86d6 100%);
  box-shadow: 0px 1px 15px 1px rgba(69, 65, 78, 0.08);
  border-radius: 5px;
}

.metric--card5 {
  background-image: linear-gradient(135deg, #80d0c7 10%, #13547a 100%);
  box-shadow: 0px 1px 15px 1px rgba(69, 65, 78, 0.08);
  border-radius: 5px;
}

.metric--card6 {
  background-image: linear-gradient(135deg, #00cdac 10%, #02aab0 100%);
  box-shadow: 0px 1px 15px 1px rgba(69, 65, 78, 0.08);
  border-radius: 5px;
}

.metric--card7 {
  background-image: linear-gradient(135deg, #868f96 10%, #596164 100%);
  box-shadow: 0px 1px 15px 1px rgba(69, 65, 78, 0.08);
  border-radius: 5px;
}

.metric--card8 {
  background-image: linear-gradient(135deg, #06beb6 10%, #48b1bf 100%);
  box-shadow: 0px 1px 15px 1px rgba(69, 65, 78, 0.08);
  border-radius: 5px;
}

.time-chart {
  border-radius: 10px;
}

.time-chart white {
  background-color: white;
}

.v-sheet--offset {
  top: -24px;
  position: relative;
}
</style>
